import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../core/store/store';
import LanguageSwitcher from './LanguageSwitcher';
import NavbarServerTime from './NavbarServerTime';
import usePageTranslation from '../hooks/usePageTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import RankingModal from '../../features/game/dashboard/components/RankingModal';
import ShopModal from '../../features/game/dashboard/components/ShopModal';

interface NavbarProps {
  onSupportClick?: () => void;
  /** Sur la page d’accueil : ouvre la modale de connexion au lieu de renvoyer vers / */
  onLoginClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onSupportClick, onLoginClick }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t: tHeader } = usePageTranslation('game', 'DashboardHeader');
  const { t: tDash } = usePageTranslation('game', 'Dashboard');
  const { t: tHome } = usePageTranslation('public', 'Home');

  const [now, setNow] = useState(() => new Date());
  const [showRanking, setShowRanking] = useState(false);
  const [showShop, setShowShop] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const clockLocale = language === 'en' ? 'en-US' : 'fr-FR';
  const headerClock = now.toLocaleString(clockLocale, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const btnSupport = 'rounded-xl border border-slate-500/50 bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-100 transition hover:bg-slate-700/80';
  const btnRanking = 'rounded-xl border border-amber-500/40 bg-amber-950/20 px-3 py-2 text-sm font-medium text-amber-100 transition hover:bg-amber-900/35';
  const btnShop = 'rounded-xl border border-green-500/40 bg-green-950/20 px-3 py-2 text-sm font-medium text-green-100 transition hover:bg-green-900/35';
  const btnLogin = 'rounded-xl border border-emerald-500/45 bg-emerald-950/25 px-3 py-2 text-sm font-medium text-emerald-100 transition hover:bg-emerald-900/35';

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-slate-600/45 bg-gradient-to-b from-slate-700/95 to-slate-900/95 backdrop-blur-md shadow-md shadow-black/25">
        <div className="mx-auto flex min-h-14 max-w-[1680px] flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 sm:px-5">
          <Link to="/" className="flex min-w-0 shrink-0 items-center gap-2.5 hover:opacity-90">
            <img
              src="/images/logo.png"
              alt={tHome('app.name')}
              className="h-9 w-9 rounded-md object-contain ring-1 ring-slate-500/40"
            />
            <div className="min-w-0 hidden sm:block">
              <p className="text-[9px] uppercase tracking-[0.22em] text-slate-300">{tHome('navbar.eyebrow')}</p>
              <p className="truncate text-sm font-bold tracking-wide text-white">OPSECOURS WEB</p>
            </div>
          </Link>

          <div className="flex w-full max-w-full flex-none flex-wrap items-center justify-end gap-3 sm:w-auto sm:max-w-[75%] lg:max-w-[70%]">
            {!isAuthenticated ? (
              <>
                <div
                  className="flex w-full max-w-full shrink-0 justify-end sm:w-auto sm:max-w-[min(100%,280px)] lg:max-w-[320px]"
                  aria-label={tDash('header.clockHint')}
                >
                  <time
                    dateTime={now.toISOString()}
                    className="w-full truncate rounded-xl border border-slate-600/50 bg-slate-900/60 px-3 py-2 text-center text-[10px] font-medium leading-snug text-slate-200 sm:w-auto sm:text-[11px]"
                    title={tDash('header.clockHint')}
                  >
                    {headerClock}
                  </time>
                </div>

                <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:gap-3">
                  <nav className="flex flex-wrap items-center justify-end gap-2" aria-label={tDash('header.navAssistance')}>
                    <Link to="/support/contact" className={btnSupport}>
                      {tHeader('support')}
                    </Link>
                  </nav>

                  <span className="hidden h-8 w-px shrink-0 bg-slate-600/55 sm:inline" aria-hidden />

                  <nav className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5" aria-label={tDash('header.navGameExtras')}>
                    <button type="button" onClick={() => setShowRanking(true)} className={btnRanking}>
                      {tHeader('ranking')}
                    </button>
                    <button type="button" onClick={() => setShowShop(true)} className={btnShop}>
                      {tHeader('shop')}
                    </button>
                  </nav>

                  <span className="hidden h-8 w-px shrink-0 bg-slate-600/55 sm:inline" aria-hidden />

                  <nav className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5" aria-label={tDash('header.sessionNav')}>
                    {onLoginClick ? (
                      <button type="button" onClick={onLoginClick} className={btnLogin}>
                        {tHeader('login')}
                      </button>
                    ) : (
                      <Link to="/" className={btnLogin}>
                        {tHeader('login')}
                      </Link>
                    )}
                    <LanguageSwitcher />
                  </nav>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-3">
                <NavbarServerTime />
                <button
                  type="button"
                  onClick={() => navigate('/panel')}
                  className="flex items-center gap-2 rounded-xl border border-slate-500/50 bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-100 transition hover:bg-slate-700/80"
                >
                  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                  <span>{tHeader('panel')}</span>
                </button>
                {onSupportClick && (
                  <button type="button" onClick={onSupportClick} className={btnSupport}>
                    {tHeader('support')}
                  </button>
                )}
                <LanguageSwitcher />
              </div>
            )}
          </div>
        </div>
      </header>

      {!isAuthenticated && (
        <>
          <RankingModal isOpen={showRanking} onClose={() => setShowRanking(false)} />
          <ShopModal isOpen={showShop} onClose={() => setShowShop(false)} />
        </>
      )}
    </>
  );
};

export default Navbar;
